import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, Eye, Link2, Trash2, UserCog } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { ROUTE_ACTIONS } from '@/app/router/paths'
import { PATHS } from '@/app/router/routes'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { queryKeys } from '@/shared/constants/query-keys'
import { useErrorHandler } from '@/shared/hooks/use-error-handler'
import { usePermissions } from '@/shared/hooks/use-permissions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { clientErrorActions } from '../../consts/client-error-actions'
import { deleteClient, markClientRevalidated } from '../../services/clients-admin.service'

interface ClientDetailActionsProps {
  clientId: string
  typePerson: 'individual' | 'juridical'
  code: number
  name: string
  /** Estado de revalidación del cliente; habilita "Marcar revalidado" solo si está vencido. */
  revalidationStatus?: string | null
  /** Hay un flujo de portal activo → se oculta "Editar cliente" (pelota de un solo lado). */
  hasActiveFlow?: boolean
  /**
   * Cómo se borra el expediente. Por defecto v1, que en el backend es admin-only.
   * La superficie v2 inyecta su propio borrado: pide el permiso `DeleteClient`,
   * que un admin tiene siempre, y así el mismo componente sirve a las dos sin
   * conocer ninguna de las dos APIs.
   */
  onDelete?: (clientId: string) => Promise<unknown>
  /** Quién ve el botón. Por defecto, sólo el admin (la regla del backend v1). */
  canDelete?: boolean
  onNavigate?: () => void
}

/** Primary actions of the panel: view, edit and share the client. */
export function ClientDetailActions({
  clientId,
  typePerson,
  code,
  name,
  revalidationStatus,
  hasActiveFlow,
  onDelete = deleteClient,
  canDelete,
  onNavigate
}: ClientDetailActionsProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAdmin } = usePermissions()
  const handleError = useErrorHandler(clientErrorActions)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isMarkOpen, setIsMarkOpen] = useState(false)

  const markMutation = useMutation({
    mutationFn: async () => await markClientRevalidated(clientId),
    onSuccess: () => {
      toast.success(`${name} marcado como revalidado`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      setIsMarkOpen(false)
    },
    onError: handleError
  })

  const go = (action: typeof ROUTE_ACTIONS.view | typeof ROUTE_ACTIONS.edit) => {
    onNavigate?.()
    navigate(PATHS.admin.clients.byType(typePerson, action, clientId))
  }

  const copyClientLink = async () => {
    const path = PATHS.admin.clients.byType(typePerson, ROUTE_ACTIONS.view, clientId)
    const url = new URL(path, window.location.origin)
    await navigator.clipboard.writeText(url.toString())
    toast.success('Enlace copiado', { duration: 1500 })
  }

  const deleteMutation = useMutation({
    mutationFn: async () => await onDelete(clientId),
    onSuccess: () => {
      toast.success(`Cliente ${name} eliminado`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      setIsDeleteOpen(false)
      onNavigate?.()
    },
    onError: handleError
  })

  return (
    <>
      <SidePanelActions
        actions={[
          {
            label: 'Ver cliente',
            icon: <Eye className="h-4 w-4" />,
            onClick: () => {
              go(ROUTE_ACTIONS.view)
            },
            primary: true
          },
          // Con un flujo de portal activo el cliente/asesor no editan los datos internos:
          // al aprobar, el writeback del portal los pisaría. Se oculta la edición.
          ...(hasActiveFlow
            ? []
            : [
                {
                  label: 'Editar cliente',
                  icon: <UserCog className="h-4 w-4" />,
                  onClick: () => {
                    go(ROUTE_ACTIONS.edit)
                  },
                  variant: 'secondary' as const
                }
              ]),
          {
            label: 'Copiar enlace',
            icon: <Link2 className="h-4 w-4" />,
            onClick: () => {
              void copyClientLink()
            },
            variant: 'ghost'
          },
          // Solo si está vencido: cierra el ciclo a mano sin enviar flujo ni correo.
          ...(revalidationStatus === 'expired'
            ? [
                {
                  label: markMutation.isPending ? 'Marcando…' : 'Marcar revalidado',
                  icon: <CalendarCheck className="h-4 w-4" />,
                  onClick: () => {
                    setIsMarkOpen(true)
                  },
                  variant: 'secondary' as const,
                  disabled: markMutation.isPending
                }
              ]
            : []),
          ...((canDelete ?? isAdmin)
            ? [
                {
                  label: deleteMutation.isPending ? 'Eliminando…' : 'Eliminar cliente',
                  icon: <Trash2 className="h-4 w-4" />,
                  onClick: () => {
                    setIsDeleteOpen(true)
                  },
                  variant: 'destructive' as const,
                  disabled: deleteMutation.isPending
                }
              ]
            : [])
        ]}
      />

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el cliente C-{String(code).padStart(8, '0')}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate()
              }}
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isMarkOpen} onOpenChange={setIsMarkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Marcar a {name} como revalidado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se registrará que este cliente fue revalidado hoy (fuera de la plataforma). Saldrá de
              &ldquo;Vencido&rdquo; y no se le enviará ningún flujo ni correo. Volverá a revalidarse
              cuando cumpla el próximo ciclo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                markMutation.mutate()
              }}
            >
              Sí, marcar revalidado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
